import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "./supabase";
import type { FilteredSetupsResponse, SetupDetail, SetupFilters, SetupListItem } from "./types";

const PAGE_SIZE = 40;

/**
 * Helper: apply common filters to a setups query, excluding one dimension.
 * Used for cascading filter options — each dimension excludes itself.
 */
function applyFilters(query: any, filters: SetupFilters, exclude?: keyof SetupFilters) {
  query = query.eq("status", "approved");
  if (exclude !== "season" && filters.season) query = query.eq("season_id", filters.season);
  if (exclude !== "week" && filters.week) query = query.eq("week_number", filters.week);
  if (exclude !== "series" && filters.series?.length) query = query.in("series_id", filters.series);
  if (exclude !== "car" && filters.car?.length) query = query.in("car_id", filters.car);
  if (exclude !== "track" && filters.track?.length) query = query.in("track_id", filters.track);
  if (exclude !== "driver" && filters.driver) query = query.eq("driver_id", filters.driver);
  if (exclude !== "freeOnly" && filters.freeOnly) query = query.eq("is_free_sample", true);
  return query;
}

export const getFilteredSetups = createServerFn({ method: "GET" })
  .inputValidator((input: SetupFilters) => input)
  .handler(async ({ data: filters }): Promise<Omit<FilteredSetupsResponse, "filters">> => {
    const db = supabaseAdmin;

    // Build main query
    let query = db.from("setups").select(
      `
        id, title, description, version, is_free_sample, download_count,
        created_at, updated_at, week_number,
        car_id, track_id, series_id, driver_id, season_id,
        car:cars!inner(id, name, name_short, image_url),
        track:tracks!inner(id, name, config),
        series:series(id, name),
        driver:profiles!setups_driver_id_fkey(id, display_name, avatar_url),
        season:seasons!inner(id, name),
        versions:setup_versions(version_number, change_notes, best_lap_time),
        files:setup_files(setup_type)
      `,
      { count: "exact" },
    );

    query = applyFilters(query, filters);

    // Sorting — primary sort by week (newest first), secondary by chosen sort.
    // For "lap_time" we still order by week server-side so pagination stays
    // grouped by week; the per-week best_lap_time ordering happens client-side
    // (best_lap_time lives on setup_versions and CLAUDE.md notes that .order()
    // on related tables is not supported).
    query = query.order("week_number", { ascending: false });
    switch (filters.sort) {
      case "downloads":
        query = query.order("download_count", { ascending: false });
        break;
      case "lap_time":
      case "newest":
      default:
        query = query.order("created_at", { ascending: false });
        break;
    }

    // Pagination
    const page = filters.page ?? 1;
    const from = (page - 1) * PAGE_SIZE;
    query = query.range(from, from + PAGE_SIZE - 1);

    const { data: setups, count, error } = await query;
    if (error) throw new Error(`Failed to fetch setups: ${error.message}`);

    // Transform to SetupListItem shape
    let items: SetupListItem[] = (setups ?? []).map((s: any) => ({
      ...s,
      latest_version: s.versions?.sort(
        (a: any, b: any) => b.version_number - a.version_number,
      )[0] ?? {
        version_number: 1,
        change_notes: null,
        best_lap_time: null,
      },
      file_types: [
        ...new Set(s.files?.filter((f: any) => f.setup_type).map((f: any) => f.setup_type)),
      ],
    }));

    // Post-fetch filter: setup_type (filter setups that have files with this type)
    // Note: this reduces the page size, so we adjust total to reflect the filtered count.
    // This is an approximation — the true filtered total would require a DB-level filter.
    if (filters.type) {
      const beforeCount = items.length;
      items = items.filter((s) => s.file_types.includes(filters.type!));
      const ratio = beforeCount > 0 ? items.length / beforeCount : 1;
      return {
        setups: items,
        total: Math.round((count ?? 0) * ratio),
      };
    }

    return {
      setups: items,
      total: count ?? 0,
    };
  });

/**
 * Separate server function for cascading filter options.
 * Uses a SINGLE query to fetch all dimension data, then computes
 * cascading filters in JavaScript. This is much faster than 7 parallel
 * queries and avoids Vercel cold start timeouts.
 */
export const getSetupFilterOptions = createServerFn({ method: "GET" })
  .inputValidator((input: SetupFilters) => input)
  .handler(async ({ data: filters }): Promise<FilteredSetupsResponse["filters"]> => {
    const db = supabaseAdmin;

    // Single query: fetch all approved setups' dimension data
    const { data: rows } = await db
      .from("setups")
      .select(
        `
        season_id, week_number, series_id, car_id, track_id, driver_id, is_free_sample,
        car:cars!inner(id, name),
        track:tracks!inner(id, name, config),
        series:series(id, name),
        driver:profiles!setups_driver_id_fkey(id, display_name),
        season:seasons!inner(id, name),
        files:setup_files(setup_type)
      `,
      )
      .eq("status", "approved");

    const allRows = rows ?? [];

    // Helper: check if a row matches all active filters EXCEPT the excluded dimension
    function matchesFilters(row: any, exclude?: keyof SetupFilters): boolean {
      if (exclude !== "season" && filters.season && row.season_id !== filters.season) return false;
      if (exclude !== "week" && filters.week && row.week_number !== filters.week) return false;
      if (exclude !== "series" && filters.series?.length && !filters.series.includes(row.series_id))
        return false;
      if (exclude !== "car" && filters.car?.length && !filters.car.includes(row.car_id))
        return false;
      if (exclude !== "track" && filters.track?.length && !filters.track.includes(row.track_id))
        return false;
      if (exclude !== "driver" && filters.driver && row.driver_id !== filters.driver) return false;
      if (exclude !== "freeOnly" && filters.freeOnly && !row.is_free_sample) return false;
      return true;
    }

    // Compute each dimension from filtered rows
    const seasonMap = new Map<string, string>();
    const weekSet = new Set<number>();
    const seriesMap = new Map<string, string>();
    const carMap = new Map<string, string>();
    const trackMap = new Map<string, string>();
    const driverMap = new Map<string, string>();
    const typeSet = new Set<string>();

    for (const row of allRows) {
      const season = Array.isArray(row.season) ? row.season[0] : row.season;
      const car = Array.isArray(row.car) ? row.car[0] : row.car;
      const track = Array.isArray(row.track) ? row.track[0] : row.track;
      const series = Array.isArray(row.series) ? row.series[0] : row.series;
      const driver = Array.isArray(row.driver) ? row.driver[0] : row.driver;

      if (matchesFilters(row, "season") && season) seasonMap.set(season.id, season.name);
      if (matchesFilters(row, "week")) weekSet.add(row.week_number);
      if (matchesFilters(row, "series") && series) seriesMap.set(series.id, series.name);
      if (matchesFilters(row, "car") && car) carMap.set(car.id, car.name);
      if (matchesFilters(row, "track") && track) {
        const label = track.config ? `${track.name} (${track.config})` : track.name;
        trackMap.set(track.id, label);
      }
      if (matchesFilters(row, "driver") && driver) driverMap.set(driver.id, driver.display_name);
      if (matchesFilters(row, "type")) {
        const files = Array.isArray(row.files) ? row.files : [];
        for (const f of files) {
          if (f.setup_type) typeSet.add(f.setup_type);
        }
      }
    }

    const toOptions = (m: Map<string, string>) =>
      [...m.entries()].map(([value, label]) => ({ value, label }));

    const typeLabels: Record<string, string> = {
      race: "Race",
      qualifying: "Qualifying",
      endurance: "Endurance",
      safe: "Safe",
      wet: "Wet",
      race_safe: "Race Safe",
      qualifying_safe: "Qualifying Safe",
      endurance_safe: "Endurance Safe",
    };

    return {
      seasons: toOptions(seasonMap),
      weeks: [...weekSet]
        .sort((a, b) => a - b)
        .map((w) => ({ value: String(w), label: `Week ${w}` })),
      series: toOptions(seriesMap),
      cars: toOptions(carMap),
      tracks: toOptions(trackMap),
      drivers: toOptions(driverMap),
      types: [...typeSet].map((t) => ({ value: t, label: typeLabels[t] ?? t })),
    };
  });

export const getSetupDetail = createServerFn({ method: "GET" })
  .inputValidator((input: { setupId: string }) => input)
  .handler(async ({ data: { setupId } }) => {
    const db = supabaseAdmin;

    const { data, error } = await db
      .from("setups")
      .select(
        `
        id, title, description, version, is_free_sample, download_count,
        created_at, week_number, series_id,
        car:cars(*),
        track:tracks(*),
        series:series(*),
        driver:profiles!setups_driver_id_fkey(id, user_id, display_name, avatar_url, bio,
          iracing_cust_id, irating_road, irating_oval, license_road, license_oval,
          achievements, social_links),
        season:seasons(*),
        versions:setup_versions(*),
        files:setup_files(*)
      `,
      )
      .eq("id", setupId)
      .eq("status", "approved")
      .single();

    if (error || !data) return null;
    return data as unknown as SetupDetail;
  });

export const getRelatedSetups = createServerFn({ method: "GET" })
  .inputValidator((input: { setupId: string; carId: string; trackId: string }) => input)
  .handler(async ({ data: { setupId, carId, trackId } }) => {
    const db = supabaseAdmin;

    const { data, error } = await db
      .from("setups")
      .select(
        `
        id, title, description, version, is_free_sample, download_count,
        created_at, updated_at, week_number,
        car:cars!inner(id, name, name_short, image_url),
        track:tracks!inner(id, name, config),
        series:series(id, name),
        driver:profiles!setups_driver_id_fkey(id, display_name, avatar_url),
        season:seasons!inner(id, name),
        versions:setup_versions(version_number, change_notes, best_lap_time),
        files:setup_files(setup_type)
      `,
      )
      .eq("status", "approved")
      .neq("id", setupId)
      .or(`car_id.eq.${carId},track_id.eq.${trackId}`)
      .order("download_count", { ascending: false })
      .limit(6);

    if (error) return [] as SetupListItem[];
    return (data ?? []).map((s: any) => {
      const versions = Array.isArray(s.versions) ? s.versions : [s.versions].filter(Boolean);
      const latestVersion = versions.sort(
        (a: any, b: any) => b.version_number - a.version_number,
      )[0];
      const files = Array.isArray(s.files) ? s.files : [s.files].filter(Boolean);
      const fileTypes = [...new Set(files.map((f: any) => f.setup_type).filter(Boolean))];
      const { versions: _, files: _f, ...rest } = s;
      return {
        ...rest,
        latest_version: latestVersion ?? {
          version_number: 1,
          best_lap_time: null,
          change_notes: null,
        },
        file_types: fileTypes,
      };
    }) as unknown as SetupListItem[];
  });

export const getCurrentSeason = createServerFn({ method: "GET" }).handler(async () => {
  const db = supabaseAdmin;
  const { data } = await db
    .from("seasons")
    .select("id, name, year, quarter, start_date, end_date, is_current")
    .eq("is_current", true)
    .maybeSingle();

  if (!data) return null;

  const startDate = new Date(data.start_date);
  const now = new Date();
  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const currentWeek = Math.max(1, Math.min(13, Math.floor(diffDays / 7) + 1));

  return { ...data, currentWeek };
});

export const getCurrentWeek = createServerFn({ method: "GET" })
  .inputValidator((input: { seasonId: string }) => input)
  .handler(async ({ data: { seasonId } }) => {
    const db = supabaseAdmin;
    const today = new Date().toISOString().split("T")[0];

    const { data } = await db
      .from("season_schedule")
      .select("week_number")
      .eq("season_id", seasonId)
      .lte("start_date", today)
      .gte("end_date", today)
      .limit(1)
      .maybeSingle();

    return data?.week_number ?? 1;
  });
